// @ts-nocheck — vendored ManualAnnotation camera-math; typechecked upstream, exempt from host strict flags (noUncheckedIndexedAccess/exactOptionalPropertyTypes).
// Parse the new egocapture_calibration_bundle/v1 (Scaramuzza / OCamCalib) into CalibrationData.
//
// Structure (top-level wrapper):
//   {
//     _id, name, serial_number, project_name, version, created_at, ...,
//     calibrations: [
//       { calibration_json: { schema_version: 'egocapture_calibration_bundle/v1',
//                             cameras: {videoC, videoFL, videoFR, videoL, videoR},
//                             stereo_pairs: {videoFL_videoFR: {...}} } },
//       { calibration_json: { calibration_json: { reference_cam, extrinsics_T_imu_cam, ... } } }  // optional Kalibr block
//     ]
//   }
//
// FL/FR intrinsics + the videoFL_videoFR stereo pair come from the bundle block.
// L/R side-cam extrinsics are derived from the Kalibr block when present
// (only needed for 4-view sessions; sessions without it work for FL+FR only).

import type { CalibrationData } from '../../types';

type Mat3 = number[][];
type Vec3 = [number, number, number];

interface ScaramuzzaCamera {
  distortion_center: [number, number];
  taylor_coefficient: number[];
  sensor_size: [number, number];
}

interface StereoPair {
  rotation_matrix: Mat3;
  translation_m: Vec3;
}

interface BundleCalibrationJson {
  cameras: Record<string, ScaramuzzaCamera>;
  stereo_pairs: Record<string, StereoPair>;
}

interface Quat {
  qx: number;
  qy: number;
  qz: number;
  qw: number;
}
interface Extrinsics extends Quat {
  px: number;
  py: number;
  pz: number;
}
interface KalibrExtrinsicsEntry {
  camera_name: string;
  extrinsics: Extrinsics;
}
interface KalibrJson {
  reference_cam: string;
  extrinsics_T_imu_cam: KalibrExtrinsicsEntry[];
}

interface BundleEntry {
  calibration_json?: BundleCalibrationJson | KalibrJson | { calibration_json: KalibrJson } | string;
  // Some entries carry the IMU-relative extrinsics as YAML text instead of
  // JSON. Format: `extrinsics:` list of `{camera_name, transform_ref_current: [tx,ty,tz,qx,qy,qz,qw]}`.
  calibration_text?: string;
  calibration_time?: string;
}

interface RawBundle {
  calibrations: BundleEntry[];
}

function mat3Mul(A: Mat3, B: Mat3): Mat3 {
  const C: Mat3 = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++) for (let k = 0; k < 3; k++) C[i][j] += A[i][k] * B[k][j];
  return C;
}

function mat3Transpose(M: Mat3): Mat3 {
  return [
    [M[0][0], M[1][0], M[2][0]],
    [M[0][1], M[1][1], M[2][1]],
    [M[0][2], M[1][2], M[2][2]],
  ];
}

function mat3MulVec3(M: Mat3, v: Vec3): Vec3 {
  return [
    M[0][0] * v[0] + M[0][1] * v[1] + M[0][2] * v[2],
    M[1][0] * v[0] + M[1][1] * v[1] + M[1][2] * v[2],
    M[2][0] * v[0] + M[2][1] * v[1] + M[2][2] * v[2],
  ];
}

// Quaternion (qx, qy, qz, qw) → 3×3 rotation matrix (Hamilton convention).
function quatToMat3(q: Quat): Mat3 {
  const { qx, qy, qz, qw } = q;
  const xx = qx * qx,
    yy = qy * qy,
    zz = qz * qz;
  const xy = qx * qy,
    xz = qx * qz,
    yz = qy * qz;
  const wx = qw * qx,
    wy = qw * qy,
    wz = qw * qz;
  return [
    [1 - 2 * (yy + zz), 2 * (xy - wz), 2 * (xz + wy)],
    [2 * (xy + wz), 1 - 2 * (xx + zz), 2 * (yz - wx)],
    [2 * (xz - wy), 2 * (yz + wx), 1 - 2 * (xx + yy)],
  ];
}

function unwrapAny(entry: BundleEntry): unknown {
  let v: unknown = entry.calibration_json;
  if (
    v == null &&
    typeof entry.calibration_text === 'string' &&
    entry.calibration_text.trim().startsWith('{')
  ) {
    v = entry.calibration_text;
  }
  v = typeof v === 'string' ? JSON.parse(v) : v;
  // Some kalibr-style entries are wrapped under a second `calibration_json` key.
  if (v && typeof v === 'object' && 'calibration_json' in v) {
    const inner = v.calibration_json;
    v = typeof inner === 'string' ? JSON.parse(inner) : inner;
  }
  return v;
}

function isBundle(v: unknown): v is BundleCalibrationJson {
  return !!v && typeof v === 'object' && 'cameras' in v && 'stereo_pairs' in v;
}

function isKalibr(v: unknown): v is KalibrJson {
  return !!v && typeof v === 'object' && 'extrinsics_T_imu_cam' in v;
}

// Parse the YAML extrinsics block carried in `calibration_text`. Used by
// EgoCapture v2 bundles that store IMU-relative camera poses as YAML instead
// of a JSON Kalibr block. Returns a KalibrJson-equivalent so callers can reuse
// the same pose-composition path.
//
// Expected fragment shape (other YAML keys may surround it):
//   extrinsics:
//   - camera_name: videoFL
//     transform_ref_current:
//     - <tx>
//     ...
//     - <qw>
//   - camera_name: videoFR
//     ...
function parseYamlExtrinsics(text: string): KalibrJson | null {
  // Capture from `extrinsics:` (column 0) up to the next top-level key.
  const blockMatch = /^extrinsics:[ \t]*\n([\s\S]*?)(?=\n[A-Za-z_][A-Za-z0-9_]*:)/m.exec(text);
  if (!blockMatch) return null;
  const block = blockMatch[1];
  const entryRe =
    /-\s*camera_name:\s*(\S+)\s*\n\s*transform_ref_current:((?:\s*\n\s*-\s*[-\d.eE+]+){7})/g;
  const entries: KalibrExtrinsicsEntry[] = [];
  let m: RegExpExecArray | null;
  while ((m = entryRe.exec(block)) !== null) {
    const nums = m[2].match(/-?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?/g)?.map(Number);
    if (!nums || nums.length !== 7 || nums.some((n) => !Number.isFinite(n))) continue;
    entries.push({
      camera_name: m[1],
      extrinsics: {
        px: nums[0],
        py: nums[1],
        pz: nums[2],
        qx: nums[3],
        qy: nums[4],
        qz: nums[5],
        qw: nums[6],
      },
    });
  }
  return entries.length > 0 ? { reference_cam: 'imu', extrinsics_T_imu_cam: entries } : null;
}

// Find the Kalibr-style sub-block among calibrations[*]. Falls back to YAML
// extrinsics in `calibration_text` if no JSON Kalibr block exists. Returns
// null if neither is present.
function findKalibr(raw: RawBundle): KalibrJson | null {
  for (const entry of raw.calibrations) {
    const v = unwrapAny(entry);
    if (isKalibr(v)) return v;
  }
  for (const entry of raw.calibrations) {
    if (typeof entry.calibration_text === 'string') {
      const k = parseYamlExtrinsics(entry.calibration_text);
      if (k) return k;
    }
  }
  return null;
}

// Find the bundle/v1 (Scaramuzza intrinsics + stereo_pairs) sub-block.
function findBundle(raw: RawBundle): BundleCalibrationJson | null {
  let best: { time: string; bundle: BundleCalibrationJson } | null = null;
  for (const entry of raw.calibrations) {
    const v = unwrapAny(entry);
    if (!isBundle(v)) continue;
    const time = entry.calibration_time ?? '';
    if (!best || time > best.time) best = { time, bundle: v };
  }
  return best?.bundle ?? null;
}

// Convert a Kalibr extrinsic (T_imu_cam: pose of cam frame expressed in IMU frame)
// to (R_imu_cam, t_imu_cam) such that P_imu = R · P_cam + t.
//
// Side-camera image-flip auto-correction: some side cameras (e.g. videoL on
// EgoCapture v2 devices) are physically mounted with the sensor rotated 180°
// around its optical axis, but the bundle/v1 intrinsics are calibrated on the
// video file AFTER it's been un-rotated for display. The YAML extrinsics still
// describe the native (rotated) sensor frame, so projecting through (YAML,
// bundle intrinsics) produces a 180°-rotated pixel. Detect this by checking
// the image-down direction (R col 1) against the IMU's +y (assumed gravity-
// down — verified by FL/FR having strongly +y col 1). If the cam's image-down
// has a strongly NEGATIVE y in the IMU frame, post-multiply R by R_z(180°) so
// the projection lines up with the displayed video.
function imuCamPose(ex: Extrinsics): { R: Mat3; t: Vec3 } {
  let R = quatToMat3(ex);
  if (R[1][1] < -0.5) {
    // R_new = R · diag(-1, -1, 1) — negates columns 0 and 1.
    R = [
      [-R[0][0], -R[0][1], R[0][2]],
      [-R[1][0], -R[1][1], R[1][2]],
      [-R[2][0], -R[2][1], R[2][2]],
    ];
  }
  return { R, t: [ex.px, ex.py, ex.pz] };
}

// Compose stereo transform src → dst from (R_imu_src, t_imu_src) and (R_imu_dst, t_imu_dst):
//   P_dst = R_imu_dst^T · R_imu_src · P_src + R_imu_dst^T · (t_imu_src − t_imu_dst).
function composeStereo(src: { R: Mat3; t: Vec3 }, dst: { R: Mat3; t: Vec3 }): { R: Mat3; T: Vec3 } {
  const Rdt = mat3Transpose(dst.R);
  const R = mat3Mul(Rdt, src.R);
  const diff: Vec3 = [src.t[0] - dst.t[0], src.t[1] - dst.t[1], src.t[2] - dst.t[2]];
  const T = mat3MulVec3(Rdt, diff);
  return { R, T };
}

function pickKey(keys: string[], needle: string): string | undefined {
  return keys.find((k) => k.toLowerCase().includes(needle));
}

export function parseBundleFormat(raw: RawBundle): CalibrationData {
  if (!Array.isArray(raw.calibrations) || raw.calibrations.length === 0) {
    throw new Error('Bundle JSON has no calibrations entries');
  }

  const bundle = findBundle(raw);
  if (!bundle) {
    throw new Error('Bundle JSON: no entry with `cameras` + `stereo_pairs` found');
  }

  const cameras = bundle.cameras;
  const stereo = bundle.stereo_pairs;
  const camKeys = Object.keys(cameras);

  // Match FL/FR by name. Order the patterns so 'fl' wins over 'l', 'fr' over 'r'.
  const flKey = pickKey(camKeys, 'fl') ?? pickKey(camKeys, 'frontleft');
  const frKey = pickKey(camKeys, 'fr') ?? pickKey(camKeys, 'frontright');
  if (!flKey || !frKey) {
    throw new Error(`Bundle JSON: cannot find FL/FR cameras among: ${camKeys.join(', ')}`);
  }

  const flCam = cameras[flKey];
  const frCam = cameras[frKey];

  const pairKey = Object.keys(stereo).find((k) => k.includes(flKey) && k.includes(frKey));
  if (!pairKey) {
    throw new Error(`Bundle JSON: no stereo pair found for ${flKey}↔${frKey}`);
  }
  const pair = stereo[pairKey];

  const result: CalibrationData = {
    cameraModel: 'scaramuzza',
    R: pair.rotation_matrix,
    T: pair.translation_m,
    flSize: [flCam.sensor_size[0], flCam.sensor_size[1]],
    frSize: [frCam.sensor_size[0], frCam.sensor_size[1]],
    flTaylor: flCam.taylor_coefficient,
    frTaylor: frCam.taylor_coefficient,
    flCenter: flCam.distortion_center,
    frCenter: frCam.distortion_center,
  };

  // ── Optional L/R side cameras ───────────────────────────────────────────
  // Need both the bundle intrinsics for L/R AND a Kalibr block to provide
  // IMU-relative extrinsics. If either is missing, the FL+FR result above
  // still works; L/R epipolar simply won't be available in the hook.

  const lKey = camKeys.find((k) => /^(video)?l$/i.test(k));
  const rKey = camKeys.find((k) => /^(video)?r$/i.test(k));
  if (!lKey && !rKey) return result;

  const kalibr = findKalibr(raw);
  if (!kalibr) {
    console.warn(
      'Bundle calibration has L/R intrinsics but no Kalibr extrinsics block; ' +
        'side-camera epipolar lines will not be available.'
    );
    return result;
  }

  const poses = new Map<string, { R: Mat3; t: Vec3 }>();
  for (const ex of kalibr.extrinsics_T_imu_cam) {
    poses.set(ex.camera_name, imuCamPose(ex.extrinsics));
  }

  const flPose = poses.get(flKey);
  const frPose = poses.get(frKey);
  if (!flPose || !frPose) return result;

  if (lKey) {
    const lPose = poses.get(lKey);
    if (lPose) {
      const lCam = cameras[lKey];
      const { R, T } = composeStereo(flPose, lPose);
      Object.assign(result, {
        lTaylor: lCam.taylor_coefficient,
        lCenter: lCam.distortion_center,
        lSize: [lCam.sensor_size[0], lCam.sensor_size[1]] as [number, number],
        R_fl_l: R,
        T_fl_l: T,
      });
    }
  }

  if (rKey) {
    const rPose = poses.get(rKey);
    if (rPose) {
      const rCam = cameras[rKey];
      const { R, T } = composeStereo(frPose, rPose);
      Object.assign(result, {
        rTaylor: rCam.taylor_coefficient,
        rCenter: rCam.distortion_center,
        rSize: [rCam.sensor_size[0], rCam.sensor_size[1]] as [number, number],
        R_fr_r: R,
        T_fr_r: T,
      });
    }
  }

  return result;
}
